import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, ReactNode } from 'react';

import type { BenchPlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { BonusChip } from '@sorare/core/src/components/card/BonusChip';
import { CardFooter as DumbCardFooter } from '@sorare/core/src/components/card/CardFooter';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';

import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';

import type { CardFooter_anyCardInterface } from './__generated__/index.graphql.ts';

const isAveragePlayerScore = (
  benchPlayerScore?: string
): benchPlayerScore is AveragePlayerScore => {
  return Object.values(AveragePlayerScore).includes(
    benchPlayerScore as AveragePlayerScore
  );
};

type Props = {
  anyCard: CardFooter_anyCardInterface;
  benchPlayerScore?: BenchPlayerScore;
  extra?: ReactNode;
} & Pick<
  ComponentProps<typeof DumbCardFooter>,
  'onClick' | 'extra' | 'matchups'
> &
  Pick<ComponentProps<typeof PlayerScore>, 'isCappedMode' | 'remainingPoints'>;

export const CardFooter = ({
  anyCard,
  benchPlayerScore,
  matchups,
  extra,
  isCappedMode,
  remainingPoints,
  onClick,
}: Props) => {
  const defaultMode = isAveragePlayerScore(benchPlayerScore)
    ? benchPlayerScore
    : AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE;
  const cappedMode = AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE;
  const mode = isCappedMode ? cappedMode : defaultMode;

  return (
    <DumbCardFooter
      left={
        <AnyPlayerStatsIcon
          backgroundColor="transparent"
          player={anyCard.anyPlayer}
        />
      }
      right={
        <BonusChip
          bonus={Number(anyCard.power) || 1}
          powerBreakdown={anyCard.powerBreakdown}
          captain={false}
        />
      }
      matchups={matchups}
      extra={extra}
      onClick={onClick}
    >
      <PlayerScore
        key={anyCard.slug}
        player={anyCard.anyPlayer}
        mode={mode as TotalAveragePlayerScore}
        isCappedMode={isCappedMode}
        remainingPoints={remainingPoints}
        animated={false}
        hideTooltip
      />
    </DumbCardFooter>
  );
};

CardFooter.fragments = {
  anyCardInterface: gql`
    fragment CardFooter_anyCardInterface on AnyCardInterface {
      slug
      anyPositions
      power
      anyPlayer {
        slug
        ...AnyPlayerStatsIcon_anyPlayer
        ...PlayerScore_anyPlayer
      }
      powerBreakdown {
        ...BonusChip_powerBreakdown
      }
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
    ${BonusChip.fragments.powerBreakdown}
  ` as TypedDocumentNode<CardFooter_anyCardInterface>,
};
